import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { meQuery } from './api/queries.ts';
import { RouteErrorNote } from './components/route-fallback.tsx';
import { watchOutboxExpiry } from './lib/outbox.ts';
import { routeTree } from './routes/tree.tsx';
import { registerServiceWorker } from './pwa.ts';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      // Bei 401/403/404 hilft erneutes Versuchen nicht.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404 || status === 410) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPendingMs: 200,
  defaultErrorComponent: RouteErrorNote,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Element #root fehlt in index.html');

registerServiceWorker((url) => void router.history.push(url));
watchOutboxExpiry();

// Aus dem Back/Forward-Cache käme die Seite samt Query-Cache zurück, nach dem Abmelden
// also mit den Daten der vorherigen Person. Neu geladen entscheidet wieder der Server.
// Ohne Sitzung bleibt der Stand: Die Schnellmeldung lebt nur im React-Zustand, und ohne
// Netz bliebe nach dem Neuladen nur die Fehlermeldung statt des Ausgangs.
window.addEventListener('pageshow', (event) => {
  if (event.persisted && queryClient.getQueryData(meQuery.queryKey)) window.location.reload();
});

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
